<template>
    <div class="grid">
        <div class="unit whole">
            <h1>View Classes</h1>
            <p class="lead">
                Edit a class name to move pupils together. For example, change the name of 'Class 1' to 'Class 2' to
                move pupils when the school year changes.<br /><a
                    href="/downloads/Changing-Classes-Guidelines.pdf"
                    download=""
                    >See guidelines</a
                >
            </p>
            <RouterLink class="btn pull-right" to="/admin/add-class">Add a new class</RouterLink>
        </div>
    </div>
    <div class="grid">
        <div class="unit whole">
            <div class="panel">
                <v-data-table
                    items-per-page="20"
                    :headers="headers"
                    :items="classes"
                    item-value="id"
                    :sort-by="sortBy"
                    class="elevation-0"
                    :custom-key-sort="customKeySort"
                    @update:sort-by="(e: VDataTable['sortBy']) => (sortBy = e)"
                >
                    <template #[`item.teachers`]="{ item }">
                        <div v-for="teacher of item.teachers" :key="`teacher-${teacher.uid}`" class="my-1">
                            {{ teacherName(teacher.uid) }}
                        </div>
                    </template>

                    <template #[`item.pupils`]="{ item }">
                        {{ item.pupils.length }}
                    </template>

                    <template #[`item.action`]="{ item }">
                        <div class="d-flex">
                            <RouterLink class="mr-3" :to="`/admin/class/${item.uid}`"
                                ><v-icon icon="mdi-square-edit-outline"></v-icon
                            ></RouterLink>
                            <a @click="deleteClass(item)"><v-icon color="error" icon="mdi-trash-can"></v-icon></a>
                        </div>
                    </template>
                </v-data-table>
            </div>
        </div>
    </div>
    <v-overlay :model-value="!isLoaded.classes || !isLoaded.teachers" class="align-center justify-center">
        <v-progress-circular color="primary" indeterminate size="64"></v-progress-circular>
    </v-overlay>
</template>
<script setup lang="ts">
import { storeToRefs } from 'pinia';
import type { VDataTable } from 'vuetify/components/VDataTable';
import type { VDataTableHeaders } from '~/types/frontend';
import { Class } from '@ilteducation/giglets-types';

definePage({ meta: { area: 'classes' } });

const store = useAdminStore();
store.fetchTeachers();
store.fetchClasses();
const { classes, isLoaded } = storeToRefs(store);
const { teacherName } = store;

const confirm = useConfirmDialog();

const sortBy = ref<VDataTable['sortBy']>([{ key: 'name', order: 'asc' }]);

const customKeySort = {
    lastActive: (a: string | undefined, b: string | undefined) => {
        const asc = sortBy.value[0].order === 'asc';
        if (!a && !b) {
            return 0;
        }
        if (!a) {
            return asc ? 1 : -1;
        }
        if (!b) {
            return asc ? -1 : 1;
        }
        return a.localeCompare(b);
    },
};

async function deleteClass(item: Class) {
    const res = await confirm(
        'Delete class',
        `Are you sure you want to delete the class ${item.name}? Pupils in the class will not be deleted.`,
    );
    if (res) {
        await store.deleteClass(item.uid);
        await store.fetchClasses();
    }
}

const headers: VDataTableHeaders = [
    { title: 'Name', key: 'name' },
    { title: 'Teachers', key: 'teachers', sortable: false },
    { title: 'Pupils', key: 'pupils', align: 'end', width: 10 },
    { title: '', key: 'action' },
];
</script>
<style scoped lang="scss">
.v-table .v-icon {
    font-size: 1.1rem;
}
</style>
